import React, { useEffect, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import LoadMask from "Utils/LoadMask/LoadMask";
import Paginacion from "Utils/Components/Paginacion/Paginacion";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { useForm } from "react-hook-form";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { CierresTabApi } from "./api/CierresTabApi";
import { FiltrosCierres } from "./components/FiltrosCierres";
import { CierresTable } from "./components/CierresTable";
import { getErrorFromResponse } from "../../../../../../../../../utility/utils";
import { ESTADOS_CIERRE_INVENTARIO_BODEGA } from "../../../../../../../../../utility/constants";

export const CierresTab = ({ storageKey, navigateToCreacionCierre }) => {
    const STORAGE_KEY = storageKey;
    const savedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    const filtros = savedState && savedState.filtros ? savedState.filtros : {};

    const fechaInicialDefault = filtros.fecha_inicial
        ? moment(filtros.fecha_inicial)
        : null;

    const fechaFinalDefault = filtros.fecha_final
        ? moment(filtros.fecha_final)
        : null;

    const { control, watch } = useForm({
        defaultValues: {
            fecha_inicial: fechaInicialDefault,
            fecha_final: fechaFinalDefault,
        },
    });

    const [cierres, setCierres] = useState([]);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(
        savedState.pagina || 1,
    );
    const [cantidadResultados, setCantidadResultados] = useState(0);

    const fecha_inicial = watch("fecha_inicial");
    const fecha_final = watch("fecha_final");

    const [hayInventarioInicial, setHayInventarioInicial] = useState(false);
    const [hayCierres, setHayCierres] = useState(false);

    const {
        getCierres,
        abrirCierre,
        getInventarioInicial,
        cancelarCierre,
        reabrirCierre,
        descargarCierreExcel,
        cargando,
    } = CierresTabApi();

    // no se puede cerrar sin inventario inicial configurado, así que hasta que
    // exista solo se ofrece configurarlo
    const GetInventarioInicial = async () => {
        try {
            const response = await getInventarioInicial();

            setHayInventarioInicial(
                !!(response && response.inventario_inicial),
            );

            // el documento deja de ser editable en cuanto hay un cierre
            setHayCierres(!!(response && !response.editable));
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const GetCierres = async () => {
        try {
            const params = {
                page: paginaSeleccionada,
            };

            // el corte es una fecha, no una hora: se envía el día completo
            if (fecha_inicial) {
                params.fecha_inicial =
                    moment(fecha_inicial).format("YYYY-MM-DD");
            }

            if (fecha_final) {
                params.fecha_final = moment(fecha_final).format("YYYY-MM-DD");
            }

            const response = await getCierres(params);
            setCierres(response.results);
            setCantidadResultados(response.count);
        } catch (error) {
            // Si el error es por una página inválida, volvemos a la página anterior
            if (
                error &&
                error.detail === "Invalid page." &&
                paginaSeleccionada > 1
            ) {
                setPaginaSeleccionada((prev) => prev - 1);
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener los cierres de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setCierres([]);
            setCantidadResultados(0);
        }
    };

    // solo puede haber un documento en proceso: mientras exista, el botón
    // lleva a trabajarlo en vez de abrir otro
    const cierreEnProceso = cierres.find(
        (item) => item.estado === ESTADOS_CIERRE_INVENTARIO_BODEGA.ABIERTO,
    );

    const AbrirCierre = async () => {
        try {
            await abrirCierre();

            NotificationManager.success(
                "Cierre de inventario iniciado",
                "Éxito",
                3000,
            );

            navigateToCreacionCierre();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al iniciar el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const CancelarCierre = async (cierre) => {
        try {
            await cancelarCierre(cierre.id);

            NotificationManager.success(
                "Cierre de inventario cancelado",
                "Éxito",
                3000,
            );

            GetCierres();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al cancelar el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 6000);
        }
    };

    const ConfirmarCancelacion = (cierre) => {
        let mensaje = "Se descartará el cierre en proceso con todo lo que ";
        mensaje += "haya registrado en él. Las existencias de bodega no ";
        mensaje += "cambian. ¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, cancelar", "Regresar", () =>
            CancelarCierre(cierre),
        );
    };

    // el cierre reabierto vuelve a estar en proceso y se continúa desde el
    // botón del listado
    const ReabrirCierre = async (cierre) => {
        try {
            await reabrirCierre(cierre.id);

            NotificationManager.success(
                "Cierre de inventario reabierto",
                "Éxito",
                3000,
            );

            GetCierres();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al reabrir el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 6000);
        }
    };

    const ConfirmarReapertura = (cierre) => {
        let mensaje = `Se reabrirá el cierre ${cierre.numero} con corte al `;
        mensaje += `${moment(cierre.fecha_corte).format("DD/MM/YYYY - HH:mm")}. `;
        mensaje += "Sus reajustes y observaciones se conservarán, y los ";
        mensaje += "reajustes se aplicarán nuevamente al cerrarlo. ";
        mensaje += "¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, reabrir", "Cancelar", () =>
            ReabrirCierre(cierre),
        );
    };

    const DescargarExcel = async (cierre) => {
        try {
            await descargarCierreExcel(cierre);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al exportar el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    useEffect(() => {
        GetInventarioInicial();
    }, []);

    useEffect(() => {
        GetCierres();
    }, [paginaSeleccionada]);

    useEffect(() => {
        GetCierres();
    }, [fecha_inicial, fecha_final]);

    useEffect(() => {
        const stateToSave = {
            filtros: {
                fecha_inicial,
                fecha_final,
            },
            pagina: paginaSeleccionada,
        };

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }, [fecha_inicial, fecha_final, paginaSeleccionada]);

    return (
        <div className="inventario-bodega__tab-wrapper">
            <LoadMask loading={cargando} blur>
                <CardEmpty noShadow>
                    <div className="d-flex justify-content-between align-items-end flex-wrap gap-1rem mb-3">
                        <FiltrosCierres
                            control={control}
                            onRefresh={() => {
                                GetCierres();
                                GetInventarioInicial();
                            }}
                            resetPage={() => setPaginaSeleccionada(1)}
                        />
                        <div className="d-flex flex-wrap align-items-center gap-1rem">
                            {!hayInventarioInicial && !hayCierres && (
                                <span className="text-danger text-xsm">
                                    Configure el inventario inicial para poder
                                    cerrar.
                                </span>
                            )}
                            {(hayInventarioInicial || hayCierres) &&
                                (cierreEnProceso ? (
                                    <button
                                        className="btn btn-primary"
                                        onClick={navigateToCreacionCierre}
                                    >
                                        Continuar cierre en proceso
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn-primary"
                                        onClick={AbrirCierre}
                                    >
                                        Cerrar inventario
                                    </button>
                                ))}
                        </div>
                    </div>

                    <CierresTable
                        cierres={cierres}
                        onCancelar={ConfirmarCancelacion}
                        onReabrir={ConfirmarReapertura}
                        onDescargarExcel={DescargarExcel}
                    />
                    <Paginacion
                        cantidadResultados={cantidadResultados}
                        paginaSeleccionada={paginaSeleccionada}
                        setPaginaSeleccionada={setPaginaSeleccionada}
                    />
                </CardEmpty>
            </LoadMask>
        </div>
    );
};
